import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { documentDeadline } from '../../utils/documentDeadline';
import { getIntervalMonths, isOverrideApplicable, UserRequirementOverride } from '../../utils/overrideUtils';
import {
  AttentionDefinition,
  AttentionItem,
  buildAttentionItems,
  documentMonthKey,
  MONTH_CODES,
  periodLabel,
  SupplierDocument,
} from '../supplier/supplierStatus';
import { OBLIGATION_BY_ID, ObligationId, OBLIGATIONS, obligationsForDefinition } from './legalObligations';

const DAY = 86400000;
export const UPCOMING_WINDOW_DAYS = 45;

export interface ComplianceDefinition extends AttentionDefinition {
  is_moral: boolean | null;
}

export interface ComplianceDocument extends SupplierDocument {
  approval_timestamp?: string | null;
}

export interface ComplianceSupplier {
  id: string;
  name: string;
  email: string;
  organization: string | null;
  is_moral: boolean | null;
  created_at: string | null;
  requirements: Array<{ definition_id: string; documents: ComplianceDocument[] }>;
  overrides: Array<Pick<UserRequirementOverride, 'definition_id' | 'renewal_type' | 'renewal_interval'>>;
}

/** State of one obligation for one supplier. */
export type ObligationState = 'ok' | 'in_review' | 'attention' | 'none';

export interface SupplierRisk {
  id: string;
  name: string;
  email: string;
  organization: string | null;
  score: number;
  obligations: Record<ObligationId, ObligationState>;
  items: Array<AttentionItem & { obligations: ObligationId[] }>;
  inReview: number;
  /** Days since the oldest open item became actionable (rejection or missed deadline). */
  oldestOpenDays: number | null;
}

export interface UpcomingDeadline {
  key: string;
  date: string;
  definitionName: string;
  period: string;
  obligations: ObligationId[];
  missing: number;
  applicable: number;
}

export interface ProblemArea {
  definitionName: string;
  obligations: ObligationId[];
  rejected: number;
  total: number;
}

export interface Responsiveness {
  rejections: number;
  resubmitted: number;
  medianDays: number | null;
}

export interface ComplianceSummary {
  suppliers: SupplierRisk[];
  exposure: Record<ObligationId, number>;
  upcoming: UpcomingDeadline[];
  problems: ProblemArea[];
  responsiveness: Responsiveness;
}

const SEVERITY: Record<string, number> = { rejected: 1, overdue: 1, correction_requested: 0.6 };
const isActive = (doc: ComplianceDocument) => !doc.is_deleted && doc.status !== 'reemplazado' && doc.status !== 'replaced';
const isNegative = (doc: ComplianceDocument) => doc.status === 'rejected' || Boolean(doc.rejection_reason);
const median = (values: number[]) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

export function definitionsForSupplier(definitions: ComplianceDefinition[], supplier: Pick<ComplianceSupplier, 'is_moral'>) {
  return definitions.filter((d) => d.is_moral === null || d.is_moral === undefined || d.is_moral === supplier.is_moral);
}

export function assessSupplier(supplier: ComplianceSupplier, definitions: ComplianceDefinition[], today: Date): SupplierRisk {
  const applicable = definitionsForSupplier(definitions, supplier);
  const obligationsOf = new Map(applicable.map((d) => [d.id, obligationsForDefinition(d.name)]));
  const items = buildAttentionItems({
    definitions: applicable,
    requirements: supplier.requirements,
    overrides: supplier.overrides,
    since: supplier.created_at,
    today,
  }).map((item) => ({ ...item, obligations: obligationsOf.get(item.definitionId) || ['otros' as ObligationId] }));

  const obligations = Object.fromEntries(OBLIGATIONS.map((o) => [o.id, 'none'])) as Record<ObligationId, ObligationState>;
  applicable.forEach((d) => (obligationsOf.get(d.id) || []).forEach((o) => { obligations[o] = 'ok'; }));
  let inReview = 0;
  supplier.requirements.forEach((req) => {
    const pending = req.documents.filter((doc) => isActive(doc) && doc.status === 'pending_approval' && !doc.rejection_reason).length;
    if (!pending || !obligationsOf.has(req.definition_id)) return;
    inReview += pending;
    (obligationsOf.get(req.definition_id) || []).forEach((o) => { if (obligations[o] === 'ok') obligations[o] = 'in_review'; });
  });
  items.forEach((item) => item.obligations.forEach((o) => { obligations[o] = 'attention'; }));

  const decidedAt = new Map<string, string>();
  supplier.requirements.forEach((req) => req.documents.forEach((doc) => { if (doc.approval_timestamp) decidedAt.set(doc.id, doc.approval_timestamp); }));
  const openSince = items
    .map((item) => (item.status === 'overdue' ? item.deadline : item.documentId ? decidedAt.get(item.documentId) : undefined))
    .filter(Boolean)
    .map((date) => Math.floor((today.getTime() - Date.parse(date as string)) / DAY));

  const score = items.reduce(
    (sum, item) => sum + (SEVERITY[item.status] ?? 0.5) * Math.max(...item.obligations.map((o) => OBLIGATION_BY_ID[o].weight)),
    0,
  );
  return {
    id: supplier.id,
    name: supplier.name,
    email: supplier.email,
    organization: supplier.organization,
    score: Math.round(score * 10) / 10,
    obligations,
    items,
    inReview,
    oldestOpenDays: openSince.length ? Math.max(0, ...openSince) : null,
  };
}

/** Periods whose delivery deadline falls in the next window, with how many suppliers have not delivered yet. */
export function upcomingDeadlines(suppliers: ComplianceSupplier[], definitions: ComplianceDefinition[], today: Date, windowDays = UPCOMING_WINDOW_DAYS): UpcomingDeadline[] {
  const todayKey = format(today, 'yyyy-MM-dd');
  const endKey = format(new Date(today.getTime() + windowDays * DAY), 'yyyy-MM-dd');
  const result: UpcomingDeadline[] = [];
  definitions.forEach((definition) => {
    const cadence = getIntervalMonths(definition.renewal_type, definition.renewal_interval);
    if (!cadence || cadence > 12 || 12 % cadence !== 0) return;
    for (const year of [today.getFullYear() - 1, today.getFullYear()]) {
      for (let start = 1; start <= 12; start += cadence) {
        const date = documentDeadline({ applicableMonth: MONTH_CODES[start - 1], applicableYear: year, renewalType: definition.renewal_type, renewalInterval: definition.renewal_interval }, 1);
        if (!date || date < todayKey || date > endKey) continue;
        const months = new Set(Array.from({ length: cadence }, (_, i) => `${year}-${String(start + i).padStart(2, '0')}`));
        let applicable = 0;
        let missing = 0;
        suppliers.forEach((supplier) => {
          if (!definitionsForSupplier([definition], supplier).length) return;
          const override = supplier.overrides.find((o) => o.definition_id === definition.id);
          if (override && isOverrideApplicable(override, definition.renewal_type, definition.renewal_interval)) return;
          if (supplier.created_at && format(new Date(year, start - 1 + cadence, 0), 'yyyy-MM-dd') < supplier.created_at.slice(0, 10)) return;
          applicable++;
          const docs = supplier.requirements.filter((r) => r.definition_id === definition.id).flatMap((r) => r.documents);
          const delivered = docs.some((doc) => isActive(doc) && doc.status !== 'rejected' && months.has(documentMonthKey(doc)));
          if (!delivered) missing++;
        });
        if (!applicable) continue;
        result.push({
          key: `${definition.id}:${year}-${start}`,
          date,
          definitionName: definition.name,
          period: periodLabel(cadence, year, start),
          obligations: obligationsForDefinition(definition.name),
          missing,
          applicable,
        });
      }
    }
  });
  return result.sort((a, b) => a.date.localeCompare(b.date) || b.missing - a.missing);
}

export function problemAreas(suppliers: ComplianceSupplier[], definitions: ComplianceDefinition[], today: Date, days = 180): ProblemArea[] {
  const since = today.getTime() - days * DAY;
  const names = new Map(definitions.map((d) => [d.id, d.name]));
  const byDefinition = new Map<string, { rejected: number; total: number }>();
  suppliers.forEach((s) => s.requirements.forEach((req) => req.documents.forEach((doc) => {
    if (doc.is_deleted || Date.parse(doc.upload_date) < since || !names.has(req.definition_id)) return;
    const row = byDefinition.get(req.definition_id) || { rejected: 0, total: 0 };
    row.total++;
    if (isNegative(doc)) row.rejected++;
    byDefinition.set(req.definition_id, row);
  })));
  return Array.from(byDefinition, ([id, row]) => ({ definitionName: names.get(id)!, obligations: obligationsForDefinition(names.get(id)), ...row }))
    .filter((row) => row.rejected > 0)
    .sort((a, b) => b.rejected / b.total - a.rejected / a.total || b.rejected - a.rejected);
}

/** How fast suppliers upload a new version for the same period after a rejection. */
export function supplierResponsiveness(suppliers: ComplianceSupplier[], today: Date, days = 180): Responsiveness {
  const since = today.getTime() - days * DAY;
  const waits: number[] = [];
  let rejections = 0;
  suppliers.forEach((s) => s.requirements.forEach((req) => {
    req.documents.forEach((doc) => {
      if (doc.status !== 'rejected' || !doc.approval_timestamp || Date.parse(doc.approval_timestamp) < since) return;
      rejections++;
      const next = req.documents
        .filter((other) => other.id !== doc.id && documentMonthKey(other) === documentMonthKey(doc) && Date.parse(other.upload_date) > Date.parse(doc.upload_date))
        .map((other) => Date.parse(other.upload_date))
        .sort((a, b) => a - b)[0];
      if (next) waits.push(Math.max(0, (next - Date.parse(doc.approval_timestamp)) / DAY));
    });
  }));
  return { rejections, resubmitted: waits.length, medianDays: median(waits) };
}

export function summarizeCompliance(suppliers: ComplianceSupplier[], definitions: ComplianceDefinition[], today = new Date()): ComplianceSummary {
  const risks = suppliers.map((s) => assessSupplier(s, definitions, today)).sort((a, b) => b.score - a.score || a.name.localeCompare(b.name, 'es'));
  const exposure = Object.fromEntries(OBLIGATIONS.map((o) => [o.id, risks.filter((r) => r.obligations[o.id] === 'attention').length])) as Record<ObligationId, number>;
  return {
    suppliers: risks,
    exposure,
    upcoming: upcomingDeadlines(suppliers, definitions, today),
    problems: problemAreas(suppliers, definitions, today).slice(0, 6),
    responsiveness: supplierResponsiveness(suppliers, today),
  };
}

// ─── Data loading ───────────────────────────────────────────

async function pages<T>(build: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; offset < 100000; offset += 1000) {
    const { data, error } = await build(offset, offset + 999);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < 1000) return rows;
  }
  throw new Error('El volumen excede el límite del resumen');
}

export async function getComplianceData(today = new Date()): Promise<ComplianceSummary> {
  const [definitions, users, memberships, overrides] = await Promise.all([
    pages<ComplianceDefinition & { is_required: boolean | null }>((a, b) =>
      supabase.from('document_definitions').select('id,name,renewal_type,renewal_interval,is_moral,is_required').eq('is_active', true).order('id').range(a, b) as never),
    pages<{ id: string; full_name: string | null; email: string; is_moral: boolean | null; created_at: string | null }>((a, b) =>
      supabase.from('users').select('id,full_name,email,is_moral,created_at').eq('role', 'normal').eq('is_active', true).eq('is_internal', false).eq('is_deleted', false).order('id').range(a, b) as never),
    pages<{ user_id: string; is_main: boolean | null; organization: { name: string } | null }>((a, b) =>
      supabase.from('user_organization').select('user_id,is_main,organization:organizations(name)').eq('is_active', true).order('id').range(a, b) as never),
    pages<{ user_id: string; definition_id: string; renewal_type: UserRequirementOverride['renewal_type']; renewal_interval: string | null }>((a, b) =>
      supabase.from('user_requirement_overrides').select('user_id,definition_id,renewal_type,renewal_interval').order('id').range(a, b) as never),
  ]);
  const supplierIds = new Set(users.map((u) => u.id));
  const requirements = await pages<{ user_id: string; definition_id: string; documents: ComplianceDocument[] | null }>((a, b) =>
    supabase
      .from('user_document_requirements')
      .select('user_id,definition_id,documents:documents!documents_requirement_id_fkey(id,status,rejection_reason,upload_date,approval_timestamp,applicable_month,applicable_year,is_deleted)')
      .order('id')
      .range(a, b) as never);

  const organizationOf = new Map<string, string>();
  memberships
    .sort((a, b) => Number(Boolean(a.is_main)) - Number(Boolean(b.is_main)))
    .forEach((m) => { if (m.organization?.name) organizationOf.set(m.user_id, m.organization.name); });
  const suppliers: ComplianceSupplier[] = users.map((u) => ({
    id: u.id,
    name: u.full_name || u.email,
    email: u.email,
    organization: organizationOf.get(u.id) || null,
    is_moral: u.is_moral,
    created_at: u.created_at,
    requirements: [],
    overrides: overrides.filter((o) => o.user_id === u.id),
  }));
  const byId = new Map(suppliers.map((s) => [s.id, s]));
  requirements.forEach((req) => {
    if (!supplierIds.has(req.user_id)) return;
    byId.get(req.user_id)!.requirements.push({ definition_id: req.definition_id, documents: req.documents || [] });
  });
  // Optional definitions are shown to suppliers but do not count as gaps.
  return summarizeCompliance(suppliers, definitions.filter((d) => d.is_required !== false), today);
}
